import * as React from "react";

import { createNotifier, errorNotification } from "@/react/notifications";
import { handleMutationResponse as createMutationResponseHandler } from "@/react/services/api/handleMutationResponse";
import { KEY } from "@/react/components/table/useTableMutations";

import { useGetTenantAudioFilesQuery } from "@/react/services/api/tenantApi";
import {
  useCreateInboundQueuesMutation,
  useDeleteQueuesMutation,
  useUpdateInboundQueuesMutation,
} from "@/react/features/vcc/common/api/queues";
import transformInboundQueuesData from "./transformInboundQueuesData";
import { withAudioFileId } from "./audioFileTransforms";
import schema from "./schema";

const createErrorHandler = (message) => {
  return (error) => {
    console.error(`${message}:`, error);
    errorNotification(error);
  };
};

const ENTITY_NAME = "inbound phone queue";

const useInboundQueuesMutations = (tenantName, mutations) => {
  const {
    rowsToCreate,
    setRowsToCreate,
    rowsToUpdate,
    setRowsToUpdate,
    rowsToDelete,
    setRowsToDelete,
    setData,
    resetData,
    addRows,
  } = mutations;
  const [createInboundQueues, { isLoading: isCreating }] =
    useCreateInboundQueuesMutation();
  const [updateInboundQueues, { isLoading: isUpdating }] =
    useUpdateInboundQueuesMutation();
  const [deleteQueues, { isLoading: isDeleting }] = useDeleteQueuesMutation();
  const { data: audioFiles } = useGetTenantAudioFilesQuery({
    tenantName,
  });

  const onCreateResponseHandler = React.useMemo(
    () =>
      createMutationResponseHandler({
        rows: rowsToCreate,
        setRows: setRowsToCreate,
        setData,
        responseMappingKey: schema.fields.id.spec.key,
        idKey: KEY,
        customFallbackKey: schema.fields.name.spec.key,
        notifyContent: createNotifier("create", ENTITY_NAME),
        isVoidResponse: false,
      }),
    [rowsToCreate, setRowsToCreate, setData]
  );
  const onUpdateResponseHandler = React.useMemo(
    () =>
      createMutationResponseHandler({
        rows: rowsToUpdate,
        setRows: setRowsToUpdate,
        setData,
        responseMappingKey: schema.fields.id.spec.key,
        idKey: KEY,
        customFallbackKey: schema.fields.name.spec.key,
        notifyContent: createNotifier("update", ENTITY_NAME),
        isVoidResponse: false,
      }),
    [rowsToUpdate, setRowsToUpdate, setData]
  );
  const onDeleteResponseHandler = React.useMemo(
    () =>
      createMutationResponseHandler({
        rows: rowsToDelete,
        setRows: setRowsToDelete,
        setData,
        responseMappingKey: schema.fields.id.spec.key,
        idKey: KEY,
        customFallbackKey: schema.fields.name.spec.key,
        notifyContent: createNotifier("delete", ENTITY_NAME),
        isVoidResponse: true,
      }),
    [rowsToDelete, setRowsToDelete, setData]
  );

  const onSave = React.useCallback(() => {
    if (rowsToCreate.size > 0) {
      const inboundQueues = transformInboundQueuesData(rowsToCreate, true);
      createInboundQueues({
        tenantName,
        inboundQueues: withAudioFileId({ audioFiles, inboundQueues }),
      })
        .unwrap()
        .then(onCreateResponseHandler)
        .catch(createErrorHandler("Failed to create inbound queues"));
    }

    if (rowsToUpdate.size > 0) {
      const inboundQueues = transformInboundQueuesData(rowsToUpdate);
      updateInboundQueues({
        tenantName,
        inboundQueues: withAudioFileId({ audioFiles, inboundQueues }),
      })
        .unwrap()
        .then(onUpdateResponseHandler)
        .catch(createErrorHandler("Failed to update inbound queues"));
    }

    if (rowsToDelete.size > 0) {
      const queueIds = Array.from(rowsToDelete.keys());
      deleteQueues({ tenantName, queueIds })
        .unwrap()
        .then(onDeleteResponseHandler)
        .catch(createErrorHandler("Failed to delete inbound queues"));
    }
  }, [
    rowsToCreate,
    rowsToUpdate,
    rowsToDelete,
    createInboundQueues,
    updateInboundQueues,
    deleteQueues,
    onCreateResponseHandler,
    onUpdateResponseHandler,
    onDeleteResponseHandler,
  ]);

  // TODO: Fix the underlying issues as part of TAP-2806:
  //  - addRows' default implementation does now know what the primary key is just from the Yup schema
  //  - the _key attribute is indeed set but we don't move it into the primary key
  //  - TAP could crash if it deserializes an object sent with a "_key" attribute
  const addRowsWithUuid = React.useCallback(
    (rowsToAdd, _schema) => {
      const idKey = schema.fields.id.spec.key;
      const rowsWithUuid = rowsToAdd.map((row) => ({
        ...row,
        [idKey]: crypto.randomUUID(),
      }));

      addRows(rowsWithUuid, _schema);
    },
    [addRows]
  );

  return {
    onSave,
    onCancel: resetData,
    isMutationOngoing: isUpdating || isCreating || isDeleting,
    filteredMutations: {
      ...mutations,
      addRows: addRowsWithUuid,
    },
    isDirty:
      rowsToCreate.size > 0 || rowsToUpdate.size > 0 || rowsToDelete.size > 0,
  };
};

export default useInboundQueuesMutations;
